import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Form } from 'react-bootstrap';
import { Icon } from '../../components/icons';

interface ChildVisibility {
    child: {
        id: number;
        name: string;
    };
    schoolYear: string;
    visible: boolean;
}

const VisibilityManager: React.FC<{ schoolYear: string, onVisibilityChange: () => void }> = ({ schoolYear, onVisibilityChange }) => {
    const [visibilities, setVisibilities] = useState<ChildVisibility[]>([]);
    const [editingChildId, setEditingChildId] = useState<number | null>(null);
    const [editingName, setEditingName] = useState('');

    useEffect(() => {
        if (schoolYear) {
            axios.get(`/api/visibility`, { params: { schoolYear } })
                .then(response => {
                    setVisibilities(response.data);
                });
        }
    }, [schoolYear]);

    const handleVisibilityChange = (childId: number, isVisible: boolean) => {
        const visibility = {
            child: { id: childId },
            schoolYear,
            visible: isVisible
        };

        axios.put('/api/visibility', visibility)
            .then(() => {
                setVisibilities(visibilities.map(v => v.child.id === childId ? { ...v, visible: isVisible } : v));
                onVisibilityChange();
            });
    };

    const startEditing = (childId: number, currentName: string) => {
        setEditingChildId(childId);
        setEditingName(currentName);
    };

    const cancelEditing = () => {
        setEditingChildId(null);
        setEditingName('');
    };

    const saveName = (childId: number) => {
        const name = editingName.trim();
        if (!name) return;
        axios.put(`/api/data/children/${childId}`, null, { params: { name } })
            .then(() => {
                setVisibilities(visibilities.map(v => v.child.id === childId ? { ...v, child: { ...v.child, name } } : v));
                setEditingChildId(null);
                setEditingName('');
                onVisibilityChange();
            });
    };

    const sorted = [...visibilities].sort((a, b) => a.child.name.localeCompare(b.child.name, 'pl'));
    const hiddenCount = visibilities.filter(v => !v.visible).length;

    return (
        <section className="section">
            <div className="surface table-card">
                <div className="table-scroll">
                    <table className="table data-table">
                        <thead>
                            <tr>
                                <th scope="col">Dziecko</th>
                                <th className="num" scope="col">Pokazuj w podsumowaniu</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.map(v => (
                                <tr key={v.child.id} className={`person-row${v.visible ? '' : ' is-hidden'}`}>
                                    <td>
                                        {editingChildId === v.child.id ? (
                                            <form
                                                className="d-flex flex-wrap align-items-center gap-2"
                                                onSubmit={e => { e.preventDefault(); saveName(v.child.id); }}
                                            >
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm w-auto"
                                                    aria-label="Imię i nazwisko"
                                                    value={editingName}
                                                    autoFocus
                                                    onChange={e => setEditingName(e.target.value)}
                                                    onKeyDown={e => { if (e.key === 'Escape') cancelEditing(); }}
                                                />
                                                <button type="submit" className="btn btn-sm btn-primary">Zapisz</button>
                                                <button type="button" className="btn btn-sm btn-secondary" onClick={cancelEditing}>Anuluj</button>
                                            </form>
                                        ) : (
                                            <span className="person-name">
                                                {v.child.name}
                                                <button
                                                    type="button"
                                                    title={`Edytuj imię i nazwisko: ${v.child.name}`}
                                                    aria-label={`Edytuj imię i nazwisko: ${v.child.name}`}
                                                    className="icon-btn"
                                                    onClick={() => startEditing(v.child.id, v.child.name)}
                                                >
                                                    <Icon name="pencil" size={15} />
                                                </button>
                                                {!v.visible && <span className="tag">ukryte</span>}
                                            </span>
                                        )}
                                    </td>
                                    <td className="num">
                                        <Form.Check
                                            type="switch"
                                            className="d-inline-block m-0"
                                            id={`visible-${v.child.id}`}
                                            aria-label={`Pokazuj w podsumowaniu: ${v.child.name}`}
                                            checked={v.visible}
                                            onChange={e => handleVisibilityChange(v.child.id, e.target.checked)}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td colSpan={2} className="text-muted fw-normal">
                                    {visibilities.length} {visibilities.length === 1 ? 'osoba' : 'osób'}
                                    {hiddenCount > 0 && ` · ukrytych: ${hiddenCount}`}
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        </section>
    );
};

export default VisibilityManager;
